import { useLazyQuery, useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { Brand, User } from "../../../../gql/__generated__/graphql";
import { UPDATE_BRAND } from "../../../apis/lambda/GraphQL/Brand/mutations";
import { GET_BRAND_WITH_REVIEWS } from "../../../apis/lambda/GraphQL/Brand/queries";
import { UPDATE_USER } from "../../../apis/lambda/GraphQL/User/mutations";
import { GET_USER_WITH_REVIEWS } from "../../../apis/lambda/GraphQL/User/queries";

export const useProfile = (id?: string) => {
  // GraphQL
  const [getUser, { data: userData, loading: userLoading }] = useLazyQuery(
    GET_USER_WITH_REVIEWS
  );
  const [getBrand, { data: brandData, loading: brandLoading }] = useLazyQuery(
    GET_BRAND_WITH_REVIEWS
  );
  const [updateUserMutation, { loading: updateLoading }] =
    useMutation(UPDATE_USER);
  const [updateBrandMutation, { loading: updateBrandLoading }] =
    useMutation(UPDATE_BRAND);

  const [profile, setProfile] = useState<User | null>(null);

  useEffect(() => {
    if (!id) {
      setProfile(null);
      return;
    }

    if (!id.includes("@")) {
      getBrand({ variables: { id: id } });
    } else if (id.includes("@")) {
      getUser({ variables: { email: id } });
    }
  }, [id, getUser, getBrand]);

  useEffect(() => {
    if (brandData?.brand) {
      const { brand } = brandData;
      const userFromBrand: User = {
        name: brand.name,
        email: "", // Not available in GET_BRAND
        userType: "BRAND_USER",
        description: brand.description,
        categories: brand.categories,
        imageUrl: brand.logo,
        rating: brand.rating,
        completedCollabs: brand.completedCollabs.toString(),
        createdAt: brand.createdAt,
        updatedAt: brand.updatedAt,
        validated: false, // Assuming false as it's not in Brand
        approved: true, // Assuming true for brands
        socialMedia: null,
        monthlyGMV: null,
        brandId: brand.id,
        tiktokData: null,
        reviews: brand.reviews,
      };
      setProfile(userFromBrand);
    } else if (userData?.user) {
      setProfile(userData.user as User);
    } else {
      setProfile(null);
    }
  }, [userData, brandData]);

  const updateProfile = async (updatedProfile: User) => {
    try {
      const { data } = await updateUserMutation({
        variables: {
          input: {
            email: updatedProfile.email,
            name: updatedProfile.name,
            description: updatedProfile.description,
            categories: updatedProfile.categories,
            imageUrl: updatedProfile.imageUrl,
            socialMedia: updatedProfile.socialMedia,
          },
        },
        // Update the cache after mutation
        refetchQueries: [
          {
            query: GET_USER_WITH_REVIEWS,
            variables: { email: updatedProfile.email },
          },
        ],
      });

      if (data?.updateUser) {
        setProfile(data.updateUser as User);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      throw error;
    }
  };

  const updateBrand = async (updatedBrand: Brand) => {
    try {
      const { data } = await updateBrandMutation({
        variables: {
          input: {
            id: updatedBrand.id,
            name: updatedBrand.name,
            logo: updatedBrand.logo,
            description: updatedBrand.description,
            categories: updatedBrand.categories,
          },
        },
        // Update the cache after mutation
        refetchQueries: [
          {
            query: GET_BRAND_WITH_REVIEWS,
            variables: { id: updatedBrand.id },
          },
        ],
      });

      if (data?.updateBrand) {
        // Convert the brand back to a User-like object for the profile state
        const updatedBrandAsUser: User = {
          name: data.updateBrand.name,
          email: "",
          userType: "BRAND_USER",
          description: data.updateBrand.description,
          categories: data.updateBrand.categories,
          imageUrl: data.updateBrand.logo,
          rating: data.updateBrand.rating,
          completedCollabs: data.updateBrand.completedCollabs.toString(),
          createdAt: data.updateBrand.createdAt,
          updatedAt: data.updateBrand.updatedAt,
          validated: false,
          approved: true,
          socialMedia: null,
          monthlyGMV: null,
          brandId: data.updateBrand.id,
          tiktokData: null,
        };
        setProfile(updatedBrandAsUser);
      }
    } catch (error) {
      console.error("Error updating brand:", error);
      throw error;
    }
  };

  return {
    profile,
    loading: userLoading || brandLoading || updateLoading || updateBrandLoading,
    updateProfile,
    updateBrand,
  };
};
